// Pieces the Dashboard and the Results page share - both drawn from GET /overview (backend services/overview.py).
import React from "react";
import { ArrowDown, ArrowUp, Box, CircleCheck, Trophy, Users } from "lucide-react";

// Local file storage gives pictures as paths on the API (/game-builder-assets/...), S3 full links (as the game builder's asset()).
const API_BASE = process.env.REACT_APP_API_URL || "";
const asset = (src) => (src && src.startsWith("/game-builder-assets") ? `${API_BASE}${src}` : src);

const ICONS = { sims: Box, learners: Users, completion: CircleCheck, success: Trophy };

export const STATUS = { published: "Published", ready: "Ready to publish", draft: "Draft" };

export const ago = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const [n, unit] = s < 3600 ? [s / 60, "minute"] : s < 86400 ? [s / 3600, "hour"] : s < 86400 * 30 ? [s / 86400, "day"]
    : s < 86400 * 365 ? [s / (86400 * 30), "month"] : [s / (86400 * 365), "year"];
  const k = Math.floor(n);
  return `${k} ${unit}${k === 1 ? "" : "s"} ago`;
};

export const pct = (v) => (v === null || v === undefined ? "–" : `${v}%`);

// "↑ 12% from the previous 30 days" for a count; percentage points for a rate. Nothing to compare with -> null.
export function Change({ now, before, rate = false, days }) {
  if (now === null || now === undefined || before === null || before === undefined) return null;
  if (!rate && before === 0) return null;
  const diff = rate ? now - before : Math.round((100 * (now - before)) / before);
  if (diff === 0) return <span className="db-change">Same as the {days} days before</span>;
  const Arrow = diff > 0 ? ArrowUp : ArrowDown;
  return (
    <span className={`db-change ${diff > 0 ? "db-up" : "db-down"}`}>
      <Arrow size={14} aria-hidden="true" /> {Math.abs(diff)}{rate ? " pts" : "%"} vs the {days} days before
    </span>
  );
}

export function StatTile({ icon: Icon, tint, label, value, children }) {
  return (
    <div className="db-card db-stat">
      <span className={`db-stat-icon db-tint-${tint}`}><Icon size={24} aria-hidden="true" /></span>
      <div>
        <div className="db-stat-label">{label}</div>
        <div className="db-stat-value">{value}</div>
        {children && <div className="db-stat-sub">{children}</div>}
      </div>
    </div>
  );
}

// The four headline numbers: simulations, and the learners of the last `days` against the `days` before.
export function HeadlineStats({ data, icons = ICONS }) {
  const { totals, this_period: now, last_period: before, days } = data;
  return (
    <div className="db-stats">
      <StatTile icon={icons.sims} tint="green" label="Total Simulations" value={totals.simulations}>
        {totals.new_this_period > 0 ? <span className="db-change db-up"><ArrowUp size={14} aria-hidden="true" /> {totals.new_this_period} new in the last {days} days</span>
          : `${totals.published} published`}
      </StatTile>
      <StatTile icon={icons.learners} tint="violet" label="Active Learners" value={now.learners}>
        <Change now={now.learners} before={before.learners} days={days} /> {now.learners === 0 && `None in the last ${days} days`}
      </StatTile>
      <StatTile icon={icons.completion} tint="green" label="Completion Rate" value={pct(now.completion)}>
        {now.completion === null ? "No plays yet" : <Change now={now.completion} before={before.completion} rate days={days} />}
      </StatTile>
      <StatTile icon={icons.success} tint="teal" label="Success Rate" value={pct(now.success)}>
        {now.success === null ? "No finished plays yet" : <Change now={now.success} before={before.success} rate days={days} />}
      </StatTile>
    </div>
  );
}

export function SimPicture({ src, className = "" }) {
  return src
    ? <img className={`db-pic ${className}`} src={asset(src)} alt="" loading="lazy" />
    : <div className={`db-pic db-pic-empty ${className}`} aria-hidden="true"><Box size={28} /></div>;
}

export const StatusChip = ({ status }) => <span className={`db-chip db-chip-${status}`}>{STATUS[status] || status}</span>;
